import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Info, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";

export default function Penjualan() {
  const navigate = useNavigate();
  const { warehouses, productMap, warehouseMap } = useMaster();
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [status, setStatus] = useState("lunas");
  const [items, setItems] = useState([{ product_id: "", qty: "", sell_price: "" }]);
  const [stockRows, setStockRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [atCostCustomer, setAtCostCustomer] = useState(false);

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  };

  useEffect(load, []);

  useEffect(() => {
    if (!warehouseId) {
      setStockRows([]);
      return;
    }
    api.get(`/reports/stock?warehouse_id=${warehouseId}`).then((r) => setStockRows(r.data.rows)).catch(() => {});
  }, [warehouseId]);

  const availMap = Object.fromEntries(stockRows.map((r) => [r.product_id, r.qty]));

  const openDialog = () => {
    setWarehouseId(warehouses[0]?.id || "");
    setCustomerName("");
    setCustomerPhone("");
    setStatus("lunas");
    setAtCostCustomer(false);
    setNote("");
    setItems([{ product_id: "", qty: "", sell_price: "" }]);
    setOpen(true);
  };

  const setItem = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    setItems(next);
    const it = next[i];
    if ((patch.product_id !== undefined || patch.qty !== undefined) && (productMap[it.product_id]?.sell_at_cost || atCostCustomer)) {
      const qty = parseInt(it.qty, 10);
      if (qty > 0) {
        api
          .get(`/stock/fifo-cost?product_id=${it.product_id}&warehouse_id=${warehouseId}&qty=${qty}`)
          .then(({ data }) => {
            if (data.qty_covered > 0) {
              setItems((cur) => cur.map((x, idx) => (idx === i ? { ...x, sell_price: String(Math.round(data.avg_cost)) } : x)));
            }
          })
          .catch(() => {});
      }
    }
  };

  const applyCogs = async (i) => {
    const it = items[i];
    const qty = parseInt(it.qty, 10);
    if (!it.product_id || !qty || qty <= 0) {
      toast.error("Select a product and quantity first");
      return;
    }
    try {
      const { data } = await api.get(
        `/stock/fifo-cost?product_id=${it.product_id}&warehouse_id=${warehouseId}&qty=${qty}`
      );
      if (!data.fully_covered) {
        toast.warning(`Only ${data.qty_covered} of ${qty} units covered by current stock`);
      }
      setItem(i, { sell_price: String(Math.round(data.avg_cost)) });
      toast.success("Price set to FIFO cost (COGS)");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const subtotal = items.reduce((s, it) => s + (parseInt(it.qty, 10) || 0) * (parseFloat(it.sell_price) || 0), 0);

  const [search, setSearch] = useState("");
  const [whFilter, setWhFilter] = useState("");
  const [note, setNote] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const validItems = items.filter((it) => it.product_id && parseInt(it.qty, 10) > 0);
    if (validItems.length === 0) {
      toast.error("Add at least one item");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        warehouse_id: warehouseId,
        customer_name: customerName,
        customer_phone: customerPhone,
        status,
        note,
        items: validItems.map((it) => ({ product_id: it.product_id, qty: parseInt(it.qty, 10), sell_price: parseFloat(it.sell_price) || 0 })),
      });
      toast.success(`Invoice ${data.inv_no} created`);
      setOpen(false);
      navigate(`/penjualan/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="penjualan-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Sales Invoices</h1>
          <p className="mt-1 text-sm text-zinc-500">Sell to customers — stock is deducted automatically via FIFO.</p>
        </div>
        <Button data-testid="btn-create-invoice" onClick={openDialog} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Create Invoice
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="invoice-search-customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer or invoice no..."
          className="h-10 w-72 rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
        />
        <select data-testid="invoice-warehouse-filter" value={whFilter} onChange={(e) => setWhFilter(e.target.value)} className="h-10 w-56 rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600">
          <option value="">All Warehouses</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
          ))}
        </select>
        {(search || whFilter) && (
          <button type="button" data-testid="invoice-search-clear" onClick={() => { setSearch(""); setWhFilter(""); }} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
            Clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="invoice-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Invoice No.</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Warehouse</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <th className="px-4 py-3 font-medium">Created By</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-zinc-400" data-testid="invoice-empty">
                  No invoices yet
                </td>
              </tr>
            )}
            {invoices
              .filter((inv) => {
                if (whFilter && inv.warehouse_id !== whFilter) return false;
                if (!search) return true;
                const q = search.toLowerCase();
                return inv.customer_name.toLowerCase().includes(q) || inv.inv_no.toLowerCase().includes(q);
              })
              .map((inv) => (
              <tr
                key={inv.id}
                onClick={() => navigate(`/penjualan/${inv.id}`)}
                className={`cursor-pointer border-b border-zinc-100 last:border-0 transition-colors hover:bg-zinc-50 ${inv.deleted ? "opacity-50" : ""}`}
                data-testid={`invoice-row-${inv.inv_no}`}
              >
                <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-600">
                  {inv.inv_no}
                  {inv.deleted && (
                    <span className="block font-sans text-[10px] font-semibold text-red-500" data-testid={`invoice-deleted-label-${inv.inv_no}`}>
                      deleted by {inv.deleted_by_name || "-"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(inv.created_at)}</td>
                <td className="px-4 py-2.5 font-medium text-zinc-900">
                  {inv.customer_name}
                  {inv.note && (
                    <span className="mt-0.5 block max-w-56 truncate rounded-sm bg-amber-50 px-1.5 py-0.5 text-[11px] font-normal text-amber-700" title={inv.note} data-testid={`invoice-note-${inv.inv_no}`}>
                      {inv.note}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{warehouseMap[inv.warehouse_id]?.name || "?"}</td>
                <td className="tnum px-4 py-2.5 text-right font-semibold text-zinc-900">{formatIDR(inv.subtotal)}</td>
                <td className="px-4 py-2.5 text-zinc-500">{inv.created_by_name || "-"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Sales Invoice</DialogTitle>
            <DialogDescription className="sr-only">Sales invoice creation form</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-sm border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="invoice-fifo-banner">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Stock is automatically taken from the oldest purchase batch first (FIFO) when the invoice is created.
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>Source Warehouse</Label>
                <select data-testid="invoice-warehouse" required value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} className={selectCls}>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{`${w.name} (${w.code})`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Customer Name</Label>
                <select
                  data-testid="invoice-customer-select"
                  value=""
                  onChange={(e) => {
                    const c = customers.find((x) => x.id === e.target.value);
                    if (c) {
                      setCustomerName(c.name);
                      setCustomerPhone(c.phone || "");
                      setAtCostCustomer(c.category === "at_cost");
                    } else {
                      setAtCostCustomer(false);
                    }
                  }}
                  className={selectCls}
                >
                  <option value="">Pick existing customer...</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{`${c.name}${c.category === "at_cost" ? " (COGS)" : ""}`}</option>
                  ))}
                </select>
                <Input data-testid="invoice-customer" required value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Or type new customer name" className="rounded-sm border-zinc-300" />
                {atCostCustomer && (
                  <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="invoice-at-cost-badge">
                    COGS PRICING ACTIVE
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input data-testid="invoice-phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="08xx" className="rounded-sm border-zinc-300" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Sales Items</Label>
                <button type="button" data-testid="invoice-add-item" onClick={() => setItems([...items, { product_id: "", qty: "", sell_price: "" }])} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                  + Add Item
                </button>
              </div>
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`invoice-item-row-${i}`}>
                  <div className="flex-1">
                    <SearchableSelect
                      testid={`invoice-item-product-${i}`}
                      options={stockRows.map((r) => ({ value: r.product_id, label: `${productMap[r.product_id]?.name} — stock: ${r.qty}` }))}
                      value={it.product_id}
                      onChange={(v) => setItem(i, { product_id: v })}
                      placeholder="Select product..."
                    />
                  </div>
                  <Input data-testid={`invoice-item-qty-${i}`} type="number" min="1" max={availMap[it.product_id] || undefined} required value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} placeholder="Qty" className="w-20 rounded-sm border-zinc-300" />
                  <Input data-testid={`invoice-item-price-${i}`} type="number" min="0" required value={it.sell_price} onChange={(e) => setItem(i, { sell_price: e.target.value })} placeholder="Sell price" className="w-36 rounded-sm border-zinc-300" />
                  <button
                    type="button"
                    data-testid={`invoice-item-cogs-${i}`}
                    onClick={() => applyCogs(i)}
                    title="Set price to FIFO cost (COGS)"
                    className="rounded-sm border border-zinc-200 px-2 py-1.5 text-[11px] font-semibold text-zinc-500 transition-colors hover:border-blue-600 hover:text-blue-600"
                  >
                    COGS
                  </button>
                  {items.length > 1 && (
                    <button type="button" data-testid={`invoice-item-remove-${i}`} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="rounded-sm p-1.5 text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              {warehouseId && stockRows.length === 0 && (
                <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="invoice-no-stock-warning">
                  This warehouse has no stock yet. Transfer stock to this warehouse first.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 items-end gap-4">
              <div className="space-y-2">
                <Label>Payment Status</Label>
                <select data-testid="invoice-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                  <option value="lunas">Paid</option>
                  <option value="belum_lunas">Unpaid</option>
                </select>
              </div>
              <div className="rounded-sm bg-zinc-50 px-4 py-2.5 text-right">
                <p className="text-xs uppercase tracking-wide text-zinc-400">Total</p>
                <p className="tnum text-xl font-bold text-zinc-900" data-testid="invoice-form-total">{formatIDR(subtotal)}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Input data-testid="invoice-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. PO number, delivery info)" className="rounded-sm border-zinc-300" />
            </div>

            <DialogFooter>
              <Button data-testid="invoice-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                <Receipt className="mr-2 h-4 w-4" /> {saving ? "Saving..." : "Create Invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
